/**
 * @fileoverview Settings helpers - 設定/統計データの正規化ユーティリティ
 */

import { getDefaultSkillType, hasSkillDefinition } from '../gameplay/skills/definitions.js';

const ALLOWED_GAME_MODES = ['Blitz', 'Classic'];
const ALLOWED_TUTORIAL_STATUSES = ['pending', 'completed', 'skipped'];

export const DEFAULT_SETTINGS = Object.freeze({
  gameMode: 'Blitz',
  selectedSkill: getDefaultSkillType(),
  tutorialOnboarding: {
    status: 'pending',
    completedAt: null,
  },
});

export const DEFAULT_STATS = Object.freeze({
  playCount: 0,
  bestScore: 0,
  longestChain: 0,
  bestMatch: 0,
});

function cloneDefaults(defaults) {
  return JSON.parse(JSON.stringify(defaults));
}

export function createDefaultSettings() {
  return cloneDefaults(DEFAULT_SETTINGS);
}

export function createDefaultStats() {
  return cloneDefaults(DEFAULT_STATS);
}

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function normalizeGameMode(value, errors) {
  if (typeof value !== 'string' || !ALLOWED_GAME_MODES.includes(value)) {
    if (value !== undefined && value !== null) {
      errors.push('gameMode must be one of the allowed values');
    }
    return DEFAULT_SETTINGS.gameMode;
  }

  return value;
}

function normalizeSelectedSkill(value, errors) {
  if (typeof value !== 'string' || !hasSkillDefinition(value)) {
    if (value !== undefined && value !== null) {
      errors.push(`Invalid skill type: ${value}, falling back to default`);
    }
    return getDefaultSkillType();
  }

  return value;
}

function normalizeTutorialStatus(value, errors) {
  if (typeof value !== 'string' || !ALLOWED_TUTORIAL_STATUSES.includes(value)) {
    if (value !== undefined && value !== null) {
      errors.push('tutorialOnboarding.status must be pending/completed/skipped');
    }
    return DEFAULT_SETTINGS.tutorialOnboarding.status;
  }

  return value;
}

function normalizeTutorialCompletedAt(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) {
    return null;
  }
  return num;
}

function normalizeTutorialOnboarding(value, errors) {
  if (!isPlainObject(value)) {
    if (value !== undefined && value !== null) {
      errors.push('tutorialOnboarding must be a plain object');
    }
    return cloneDefaults(DEFAULT_SETTINGS.tutorialOnboarding);
  }

  return {
    status: normalizeTutorialStatus(value.status, errors),
    completedAt: normalizeTutorialCompletedAt(value.completedAt),
  };
}

function normalizePlayCount(value, errors) {
  const num = Number(value);
  if (!Number.isInteger(num) || num < 0) {
    if (value !== undefined && value !== null) {
      errors.push('playCount must be a non-negative integer');
    }
    return DEFAULT_STATS.playCount;
  }

  return num;
}

function normalizeBestScore(value, errors) {
  const num = Number(value);
  if (!Number.isInteger(num) || num < 0) {
    if (value !== undefined && value !== null) {
      errors.push('bestScore must be a non-negative integer');
    }
    return DEFAULT_STATS.bestScore;
  }

  return num;
}

function normalizeLongestChain(value, errors) {
  const num = Number(value);
  if (!Number.isInteger(num) || num < 0) {
    if (value !== undefined && value !== null) {
      errors.push('longestChain must be a non-negative integer');
    }
    return DEFAULT_STATS.longestChain;
  }

  return num;
}

function normalizeBestMatch(value, errors) {
  const num = Number(value);
  if (!Number.isInteger(num) || num < 0) {
    if (value !== undefined && value !== null) {
      errors.push('bestMatch must be a non-negative integer');
    }
    return DEFAULT_STATS.bestMatch;
  }

  return num;
}

export function validateSettings(settings) {
  const errors = [];
  const sanitized = createDefaultSettings();

  if (!isPlainObject(settings)) {
    if (settings !== undefined && settings !== null) {
      errors.push('settings must be a plain object');
    }
    return { isValid: errors.length === 0, sanitized, errors };
  }

  sanitized.gameMode = normalizeGameMode(settings.gameMode, errors);
  sanitized.selectedSkill = normalizeSelectedSkill(settings.selectedSkill, errors);
  sanitized.tutorialOnboarding = normalizeTutorialOnboarding(settings.tutorialOnboarding, errors);

  return { isValid: errors.length === 0, sanitized, errors };
}

export function validateStats(stats) {
  const errors = [];
  const sanitized = createDefaultStats();

  if (!isPlainObject(stats)) {
    if (stats !== undefined && stats !== null) {
      errors.push('stats must be a plain object');
    }
    return { isValid: errors.length === 0, sanitized, errors };
  }

  sanitized.playCount = normalizePlayCount(stats.playCount, errors);
  sanitized.bestScore = normalizeBestScore(stats.bestScore, errors);
  sanitized.longestChain = normalizeLongestChain(stats.longestChain, errors);
  sanitized.bestMatch = normalizeBestMatch(stats.bestMatch, errors);

  return { isValid: errors.length === 0, sanitized, errors };
}

export function sanitizeSettings(settings) {
  return validateSettings(settings).sanitized;
}

export function sanitizeStats(stats) {
  return validateStats(stats).sanitized;
}

export function isValidSettingsKey(key) {
  return typeof key === 'string' && key in DEFAULT_SETTINGS;
}

export function isValidStatsKey(key) {
  return typeof key === 'string' && key in DEFAULT_STATS;
}

export function getAllowedGameModes() {
  return [...ALLOWED_GAME_MODES];
}
